import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { Catalog } from 'src/app/interfaces/reports.interfaces';
import {
  BoatsByModule,
  DownloadScaleResponse,
  GenericTicket,
  GenericTicketResponse,
  Scale,
  OriginTicket,
  OriginWeight,
  ScaleResponse,
  ScaleTicket,
  ScaleTicketRailwaySybasa,
  ScaleTicketTruckSybasa,
  SybasaBody,
  TareWeight,
  Trailer,
  WeighingMachinee,
  TicketsBulkUpdateGetParams,
  TicketsBulkUpdateParams,
  TicketsBulkUpdateRes
} from 'src/app/interfaces/scale-ticket.interfaces';

/**
 * Servicio que gestiona los tickets de báscula y sus entidades relacionadas
 * (barcos por módulo, básculas, supervisores, camiones, pesos tara y de origen,
 * integración con Sybasa y actualización masiva), incluyendo el cacheo de
 * catálogos y la descarga de tickets en PDF.
 */
@Injectable({
  providedIn: 'root'
})
export class ScaleTicketService {
  scaleTickets: ScaleTicket[] = [];
  scaleTicketsFilter: ScaleTicket[] = [];
  trucks: Trailer[] = [];
  trucksFilter: Trailer[] = [];

  private scaleTicketModulesCache$: Observable<Catalog[]> | null = null;
  private weighingMachineeCache$: Observable<WeighingMachinee[]> | null = null;
  private supervisorsCache$: Observable<Catalog[]> | null = null;
  private trucksCache$: Observable<Trailer[]> | null = null;

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Obtiene los barcos de un módulo, aplanando la respuesta anidada del backend.
   * @param module - Identificador del módulo.
   * @returns Observable con la lista de barcos del módulo.
   */
  getBoatsByModule(module: number):Observable<BoatsByModule[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.boatsByModule}${module}`;
    return this.http.get<BoatsByModule[][]>(url).pipe(
      map( array => {
        if (Array.isArray(array) && array.length === 1) {
          return array[0];
        } else if (Array.isArray(array) && array.length > 1) {
          return [...array[0], ...array[1], ...array[2], ...array[3]];
        }
        return [];
      } )
    );
  }

  /**
   * Obtiene todos los barcos agrupados por módulo.
   * @param module - Identificador del módulo; por defecto 4.
   * @returns Observable con la lista de barcos agrupados por módulo.
   */
  getAllBoats(module: number = 4):Observable<BoatsByModule[][]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.boatsByModule}${module}`;
    return this.http.get<BoatsByModule[][]>(url);
  }

  /**
   * Obtiene el catálogo de básculas, cacheándolo en memoria.
   * @returns Observable con la lista de básculas.
   */
  getWeighingMachinee(): Observable<WeighingMachinee[]> {
    if (!this.weighingMachineeCache$) {
      const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.weighingMachinee}`;
      this.weighingMachineeCache$ = this.http.get<WeighingMachinee[]>(url).pipe(
        shareReplay(1)
      );
    }
    return this.weighingMachineeCache$;
  }

  /**
   * Obtiene el catálogo de orígenes de ticket.
   * @returns Observable con la lista de orígenes de ticket.
   */
  getOriginsTicket(): Observable<OriginTicket[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.originTicket}`;
    return this.http.get<OriginTicket[]>(url);
  }

  /**
   * Obtiene el catálogo de supervisores, cacheándolo en memoria.
   * @returns Observable con la lista de supervisores.
   */
  getSupervisors(): Observable<Catalog[]> {
    if (!this.supervisorsCache$) {
      const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.supervisors}`;
      this.supervisorsCache$ = this.http.get<Catalog[]>(url).pipe(
        shareReplay(1)
      );
    }
    return this.supervisorsCache$;
  }

  /**
   * Obtiene el catálogo de módulos de tickets de báscula, cacheándolo en memoria.
   * @returns Observable con la lista de módulos.
   */
  getScaleTicketModules(): Observable<Catalog[]> {
    if (!this.scaleTicketModulesCache$) {
      const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.modules}`;
      this.scaleTicketModulesCache$ = this.http.get<Catalog[]>(url).pipe(
        shareReplay(1)
      );
    }
    return this.scaleTicketModulesCache$;
  }

  // Scale Ticket
  /**
   * Obtiene todos los tickets de báscula.
   * @returns Observable con la lista de tickets de báscula.
   */
  getAllScaleTickets():Observable<ScaleTicket[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.list}`;
    return this.http.get<ScaleTicket[]>(url);
  }

  /**
   * Obtiene los tickets de báscula de una fecha.
   * @param date - Fecha a consultar.
   * @returns Observable con la lista de tickets de la fecha.
   */
  getScaleTicketsByDate(date: string):Observable<ScaleTicket[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.getByDate}${date}`;
    return this.http.get<ScaleTicket[]>(url);
  }

  /**
   * Obtiene un ticket de báscula por su número.
   * @param ticket_number - Número del ticket.
   * @returns Observable con el ticket de báscula.
   */
  getScaleTicket(ticket_number: number):Observable<ScaleTicket[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.get}${ticket_number}`;
    return this.http.get<ScaleTicket[]>(url);
  }

  /**
   * Crea un ticket de báscula.
   * @param scaleTicket - Datos del ticket a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createScaleTicket(scaleTicket: ScaleTicket):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.create}`;
    return this.http.post<ScaleResponse>(url, scaleTicket);
  }

  /**
   * Actualiza un ticket de báscula.
   * @param scaleTicket - Datos del ticket a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateScaleTicket(scaleTicket: ScaleTicket):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.update}`;
    return this.http.put<ScaleResponse>(url, scaleTicket);
  }

  /**
   * Elimina un ticket de báscula.
   * @param ticket_number - Número del ticket a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteScaleTicket(ticket_number: number):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.delete}`;
    return this.http.delete<ScaleResponse>(url, { headers: new HttpHeaders(), body: { ticket_number } });
  }

  /**
   * Descarga un ticket de báscula en PDF.
   * @param ticket_number - Número del ticket.
   * @param operation_type - Tipo de operación.
   * @param module_id - Identificador del módulo o `null` si no aplica.
   * @returns Observable con la respuesta de la descarga.
   */
  downloadScaleTicket( ticket_number: number, operation_type: number, module_id: number | null ): Observable<DownloadScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.download}`;
    let body: {} = {ticket_number, operation_type};
    if (module_id !== null) {
      body = {...body, module_id};
    }
    return this.http.post<DownloadScaleResponse>(url, body);
  }

  /**
   * Descarga un ticket de báscula genérico en PDF.
   * @param genericTicket - Datos del ticket genérico a descargar.
   * @returns Observable con la respuesta de la descarga.
   */
  downloadGenericScaleTicket( genericTicket: GenericTicket ): Observable<GenericTicketResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.downloadGenericPDF}`;
    return this.http.post<GenericTicketResponse>(url, genericTicket);
  }

  // Truck
  /**
   * Obtiene el catálogo de camiones, cacheándolo en memoria.
   * @returns Observable con la lista de camiones.
   */
  getTrucks(): Observable<Trailer[]> {
    if (!this.trucksCache$) {
      const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.truck.list}`;
      this.trucksCache$ = this.http.get<Trailer[]>(url).pipe(
        shareReplay(1)
      );
    }
    return this.trucksCache$;
  }

  /**
   * Limpia la caché de camiones para forzar una nueva consulta.
   */
  clearTrucksCache(): void {
    this.trucksCache$ = null;
  }

  /**
   * Crea un camión e invalida la caché de camiones.
   * @param trailer - Datos del camión a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createTruck(trailer: Trailer): Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.truck.create}`;
    return this.http.post<ScaleResponse>(url, trailer).pipe(
      tap(() => this.clearTrucksCache())
    );
  }

  /**
   * Actualiza un camión e invalida la caché de camiones.
   * @param trailer - Datos del camión a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateTruck(trailer: Trailer): Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.truck.update}`;
    return this.http.put<ScaleResponse>(url, trailer).pipe(
      tap(() => this.clearTrucksCache())
    );
  }

  /**
   * Elimina un camión e invalida la caché de camiones.
   * @param id - Identificador del camión a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteTruck(id: number): Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.truck.delete}`;
    return this.http.delete<ScaleResponse>(url, { headers: new HttpHeaders(), body: { id: id } }).pipe(
      tap(() => this.clearTrucksCache())
    );
  }

  // Tare Weight
  /**
   * Obtiene los pesos tara que coinciden con un valor de búsqueda.
   * @param value - Valor de búsqueda.
   * @returns Observable con la lista de pesos tara.
   */
  getTareWeights(value: string):Observable<TareWeight[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.tareWeight.list}${value}`;
    return this.http.get<TareWeight[]>(url);
  }

  /**
   * Crea un peso tara.
   * @param tareWeight - Datos del peso tara a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createTareWeight(tareWeight: TareWeight):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.tareWeight.create}`;
    return this.http.post<ScaleResponse>(url, tareWeight);
  }

  /**
   * Actualiza un peso tara.
   * @param tareWeight - Datos del peso tara a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateTareWeight(tareWeight: TareWeight):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.tareWeight.update}`;
    return this.http.put<ScaleResponse>(url, tareWeight);
  }

  /**
   * Elimina un peso tara.
   * @param id - Identificador del peso tara a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteTareWeight(id: number):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.tareWeight.delete}`;
    return this.http.delete<ScaleResponse>(url, { headers: new HttpHeaders(), body: { id: id } });
  }

  // Origin weight
  /**
   * Obtiene los pesos de origen de una fecha, opcionalmente filtrados por valor.
   * @param date - Fecha a consultar.
   * @param value - Valor opcional de búsqueda.
   * @returns Observable con la lista de pesos de origen.
   */
  getOriginWeights(date: string, value?: string):Observable<OriginWeight[]> {
    let url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.originWeight.list}${date}`;
    url = value ? url.concat(`/${value}`) : url;
    return this.http.get<OriginWeight[]>(url);
  }

  /**
   * Crea un peso de origen.
   * @param originWeight - Datos del peso de origen a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createOriginWeight(originWeight: OriginWeight):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.originWeight.create}`;
    return this.http.post<ScaleResponse>(url, originWeight);
  }

  /**
   * Actualiza un peso de origen.
   * @param originWeight - Datos del peso de origen a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateOriginWeight(originWeight: OriginWeight):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.originWeight.update}`;
    return this.http.put<ScaleResponse>(url, originWeight);
  }

  /**
   * Elimina un peso de origen.
   * @param id - Identificador del peso de origen a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteOriginWeight(id: number):Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.originWeight.delete}`;
    return this.http.delete<ScaleResponse>(url, { headers: new HttpHeaders(), body: { id: id } });
  }

  // Sybasa
  /**
   * Obtiene los tickets de báscula de camión desde Sybasa.
   * @param body - Parámetros de consulta para Sybasa.
   * @returns Observable con la lista de tickets de camión de Sybasa.
   */
  getScaleTicketTruckSybasa(body: SybasaBody): Observable<ScaleTicketTruckSybasa[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.sybasa}`;
    return this.http.post<ScaleTicketTruckSybasa[]>(url, body);
  }

  /**
   * Obtiene los tickets de báscula de ferrocarril desde Sybasa.
   * @param body - Parámetros de consulta para Sybasa.
   * @returns Observable con la lista de tickets de ferrocarril de Sybasa.
   */
  getScaleTicketRailwaySybasa(body: SybasaBody): Observable<ScaleTicketRailwaySybasa[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.sybasa}`;
    return this.http.post<ScaleTicketRailwaySybasa[]>(url, body);
  }

  // Internal and external trucks
  /**
   * Activa o desactiva el manejo de camiones internos y externos.
   * @param internalExternalTrucks - Configuración de camiones internos/externos.
   * @returns Observable con la respuesta de la operación.
   */
  toggleInternalExternalTrucks(internalExternalTrucks: Scale): Observable<ScaleResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.toggleInternalExternalTrucks}`;
    return this.http.post(url, internalExternalTrucks);
  }

  // Bulk update Page
  /**
   * Obtiene los tickets disponibles para actualización masiva según los filtros.
   * @param params - Parámetros de filtrado para la actualización masiva.
   * @returns Observable con la lista de tickets a actualizar.
   */
  getTicketsForBulkUpdate(params: TicketsBulkUpdateGetParams): Observable<ScaleTicket[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.bulkUpdate.list}`;

    let httpParams = new HttpParams()
      .set('operation_type', params.operation_type.toString())
      .set('lot_number', params.lot_number.toString());

    if (params.routes_int_exp)
      httpParams = httpParams.set('routes_int_exp', params.routes_int_exp.toString());

    return this.http.get<ScaleTicket[]>(url, { params: httpParams });
  }

  /**
   * Actualiza de forma masiva un conjunto de tickets.
   * @param tickets - Datos de los tickets a actualizar.
   * @returns Observable con la respuesta de la actualización masiva.
   */
  bulkUpdateTickets(tickets: TicketsBulkUpdateParams): Observable<TicketsBulkUpdateRes> {
    const url: string = `${GLOBAL.url}${GLOBAL.scaleTicket.bulkUpdate.update}`;
    return this.http.put<TicketsBulkUpdateRes>(url, tickets);
  }
}
